
import React, { createContext, useContext, useState, useEffect } from 'react';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistLoaded, setWishlistLoaded] = useState(false);

  // Load wishlist from localStorage on component mount
  useEffect(() => {
    const loadWishlistFromStorage = () => {
      try {
        const savedWishlist = localStorage.getItem('pricepulse_wishlist');
        if (savedWishlist) {
          const parsedWishlist = JSON.parse(savedWishlist);
          if (Array.isArray(parsedWishlist)) {
            setWishlistItems(parsedWishlist);
          } else {
            console.warn('Invalid wishlist format in localStorage, resetting');
            localStorage.setItem('pricepulse_wishlist', JSON.stringify([]));
            setWishlistItems([]);
          }
        }
        setWishlistLoaded(true);
      } catch (error) {
        console.error('Error parsing wishlist from localStorage:', error);
        localStorage.removeItem('pricepulse_wishlist');
        setWishlistItems([]);
        setWishlistLoaded(true);
      }
    };

    loadWishlistFromStorage();

    // Listen for storage changes from other tabs/windows
    const handleStorageChange = (e) => {
      if (e.key === 'pricepulse_wishlist') {
        loadWishlistFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    // Only run the save logic if the initial load from storage is complete.
    if (wishlistLoaded) {
      try {
        localStorage.setItem('pricepulse_wishlist', JSON.stringify(wishlistItems));
      } catch (error) {
        console.error('Error saving wishlist to localStorage:', error);
      }
    }
  }, [wishlistItems, wishlistLoaded]); // <-- Add wishlistLoaded to the dependency array

  const addToWishlist = (product) => {
    console.log('Adding to wishlist:', product.asin);
    setWishlistItems(prevItems => {
      // Check if item already exists
      const existingItem = prevItems.find(item => item.asin === product.asin);
      if (existingItem) {
        console.log('Item already in wishlist:', product.asin);
        return prevItems; // Don't add duplicates
      }
      
      // Add new item with current date
      const newItem = {
        ...product,
        addedDate: new Date().toISOString().split('T')[0]
      };
      
      const updatedItems = [...prevItems, newItem];
      console.log('Updated wishlist items:', updatedItems.length);
      return updatedItems;
    });
  };

  const removeFromWishlist = (asin) => {
    console.log('Removing from wishlist:', asin);
    setWishlistItems(prevItems => {
      const updatedItems = prevItems.filter(item => item.asin !== asin);
      console.log('Updated wishlist items after removal:', updatedItems.length);
      return updatedItems;
    });
  };

  const clearWishlist = () => {
    console.log('Clearing entire wishlist');
    setWishlistItems([]);
  };

  const isInWishlist = (asin) => {
    return wishlistItems.some(item => item.asin === asin);
  };

  // Alternative naming for better compatibility
  const isWishlisted = (asin) => {
    return isInWishlist(asin);
  };

  const toggleWishlist = (product) => {
    if (isInWishlist(product.asin)) {
      removeFromWishlist(product.asin);
    } else {
      addToWishlist(product);
    }
  };

  const value = {
    wishlistItems,
    wishlistLoaded,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    isInWishlist,
    isWishlisted,
    toggleWishlist,
    wishlistCount: wishlistItems.length
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
